import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { get, debounce } from 'lodash';
import { Icon } from '@unite-us/ui';
import { SelectField, InputField } from '@unite-us/app-components';
import callOrLog from 'src/common/utils/callOrLog';
import { SEARCH } from 'common/utils/EventTracker/utils/eventConstants';
import { fetchSearchResults } from 'actions/Search/Group';
import { loadKeyFromSessionStorage, saveKeyToSessionStorage } from 'src/common/utils/utils';
import { goToSearchIndex } from './utils';
import SearchBarOverlay from './components/SearchBarOverlay';
import {
  DEFAULT_SEARCH_SCOPE,
  MAX_CHAR_COUNT_FOR_SEARCH,
  MIN_CHAR_COUNT_FOR_SEARCH,
  MIN_CHAR_WARNING_TEXT,
  SEARCH_DEBOUNCE_TIME,
  SEARCH_SCOPES,
  SEARCH_SCOPE_SESSION_STORAGE_KEY,
} from '../constants';
import './stylesheets/searchBar.scss';

const composeAccessibilityMessage = (totalCount, query) => {
  const message = totalCount > 0 ?
    `Found ${totalCount} results${query ? ` for "${query}"` : ''}.` :
    `No results found${query ? ` for "${query}"` : ''}.`;
  return message;
};

export class SearchBar extends Component {
  constructor(props) {
    super(props);

    const defaultQueryScope = loadKeyFromSessionStorage(SEARCH_SCOPE_SESSION_STORAGE_KEY) ||
      DEFAULT_SEARCH_SCOPE;

    this.state = {
      query: '',
      searchResults: {},
      showResultsDropdown: false,
      accessibilityMessage: '',
      searchFailed: false,
      scope: defaultQueryScope,
    };

    this.clearSearchResults = this.clearSearchResults.bind(this);
    this.closeSearchResults = this.closeSearchResults.bind(this);
    this.fetchSearchResults = this.fetchSearchResults.bind(this);
    this.debouncedFetchSearchResults = debounce(this.fetchSearchResults, SEARCH_DEBOUNCE_TIME);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleScopeChange = this.handleScopeChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSearchInputKeyDown = this.handleSearchInputKeyDown.bind(this);
  }

  getChildContext() {
    const {
      closeSearchResults,
      isFetching,
      session,
    } = this.props;

    const {
      query,
      searchResults,
      searchFailed,
    } = this.state;

    return {
      closeSearchResults,
      isFetching,
      query,
      searchResults,
      searchFailed,
      session,
    };
  }

  // eslint-disable-next-line react/sort-comp
  fetchSearchResults(query) {
    const scope = this.state.scope;
    const { groupId } = this.context;
    const { networkId, isCC } = this.props;

    if (query.length < MIN_CHAR_COUNT_FOR_SEARCH) return;

    this.props.fetchSearchResults({
      groupId,
      query,
      networkId,
      isCC,
      scope,
    }).then((searchResults) => {
      if (!searchResults?.contact) {
        this.setState({
          searchResults: {},
          accessibilityMessage: 'Search failed. Please try again.',
          searchFailed: true,
        });
        return;
      }

      const contactData = searchResults?.contact?.data ?? [];
      const totalCount = contactData.length;

      setTimeout(() => {
        this.setState({
          searchResults,
          accessibilityMessage: composeAccessibilityMessage(totalCount, query),
          searchFailed: false,
        });
      }, 300);
    }).catch(() => {
      if (this.state.query.length >= MIN_CHAR_COUNT_FOR_SEARCH) {
        this.setState({
          searchResults: {},
          accessibilityMessage: 'Search failed. Please try again.',
          searchFailed: true,
        });
      }
    });
  }

  clearSearchResults() {
    this.setState({
      searchResults: {},
      searchFailed: false,
    });
  }

  closeSearchResults() {
    this.props.closeSearchResults();
    callOrLog(() => this.context.eventTracker(SEARCH.closeSearchBar));
  }

  handleInputChange(event) {
    const query = event.target.value;

    const hasQuery = query.length > 0;
    const isSearchableQuery = query.length >= MIN_CHAR_COUNT_FOR_SEARCH;

    this.setState({ query }, () => {
      this.setState({ showResultsDropdown: hasQuery });

      if (isSearchableQuery) {
        this.debouncedFetchSearchResults(query);
        return;
      }

      this.clearSearchResults();
      this.setState({ accessibilityMessage: '', searchFailed: false });
    });
  }

  handleScopeChange(scopeValue) {
    if (scopeValue !== this.state.scope) {
      saveKeyToSessionStorage(SEARCH_SCOPE_SESSION_STORAGE_KEY, scopeValue);
      this.setState({ scope: scopeValue });
      this.fetchSearchResults(this.state.query);
    }
  }

  handleSubmit(e) {
    e.preventDefault();

    const { query, scope } = this.state;

    if (query.length < MIN_CHAR_COUNT_FOR_SEARCH) {
      this.setState({ accessibilityMessage: MIN_CHAR_WARNING_TEXT });
      return;
    }

    this.setState({ accessibilityMessage: '' });
    this.props.closeSearchResults();
    callOrLog(() => this.context.eventTracker(SEARCH.searchIndex));
    goToSearchIndex({ query, scope });
  }

  handleSearchInputKeyDown(e) {
    if (e.key === 'Enter') this.handleSubmit(e);
  }

  render() {
    return (
      <div className="search-bar">
        <div className="search-bar__form-container">
          <Icon
            icon="IconSearch"
            className="search-bar__button-search"
            color={'#FFFFFF'}
            size={20}
          />

          <form className="search-bar__form" onSubmit={this.handleSubmit}>
            <div className="flex col h-9 rounded">
              <div className="m-auto min-w-[150px] h-9">
                <SelectField
                  id="search-bar-select-field"
                  className="search-bar__select-field m-0 h-9"
                  options={SEARCH_SCOPES}
                  label="Search Scope"
                  valueKey="value"
                  labelKey="label"
                  onChange={this.handleScopeChange}
                  value={this.state.scope}
                  searchEnabled={false}
                  position="bottom"
                  hideLabel
                  pristine
                  touched
                />
              </div>

              <div
                className="m-auto min-w-[400px] w-full h-9"
                onKeyDown={this.handleSearchInputKeyDown}
              >
                <InputField
                  id="search-bar-input-field"
                  className="search-bar__input-field m-0 h-9"
                  label="Search"
                  placeholder="Search"
                  onChange={this.handleInputChange}
                  value={this.state.query}
                  maxLength={MAX_CHAR_COUNT_FOR_SEARCH}
                  autoComplete={false}
                  autoFocus
                  hideLabel
                  pristine
                  touched
                />
              </div>
            </div>
            <div
              className="sr-only"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {this.state.accessibilityMessage}
            </div>
          </form>

          <Icon
            className="search-bar__button-clear"
            color={'#FFFFFF'}
            icon="IconCross"
            onClick={this.closeSearchResults}
            size={18}
            ariaLabel="Close client search bar"
          />

          <SearchBarOverlay
            showResultsDropdown={this.state.showResultsDropdown}
            scope={this.state.scope}
          />
        </div>
      </div>
    );
  }
}

SearchBar.propTypes = {
  closeSearchResults: PropTypes.func.isRequired,
  fetchSearchResults: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
  networkId: PropTypes.string,
  isCC: PropTypes.bool,
};

SearchBar.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
};

SearchBar.childContextTypes = {
  closeSearchResults: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  query: PropTypes.string.isRequired,
  searchResults: PropTypes.object.isRequired,
  searchFailed: PropTypes.bool.isRequired,
  session: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    isFetching: get(state, 'search.isFetching', false),
    session: get(state, 'session', {}),
    networkId: get(state, 'session.networkId'),
    isCC: get(state, 'session.isCoordinationGroup', false),
  };
}

export default connect(
  mapStateToProps,
  { fetchSearchResults },
)(SearchBar);
